// src/products/capRaise/pages/DealDocsPage.js
// DealDocs page - integrates with Dataroom module for document management

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  Upload, 
  Settings, 
  Plus, 
  ExternalLink,
  Users,
  Folder,
  Eye,
  Download,
  X
} from 'lucide-react';
import { supabase } from '../../../config';
import { useNavigate } from 'react-router-dom';

const DealDocsPage = ({ 
  user, 
  selectedCompany, 
  activeRound, 
  addMessage 
}) => {
  const navigate = useNavigate();
  const [dataroomProject, setDataroomProject] = useState(null);
  const [projectStats, setProjectStats] = useState({
    totalDocuments: 0,
    totalViews: 0,
    totalDownloads: 0,
    storageUsed: 0
  });
  const [loading, setLoading] = useState(true);
  const [creatingProject, setCreatingProject] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);

  // Load or create dataroom project for this round
  const loadDataroomProject = useCallback(async () => {
    if (!activeRound) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // First, check if there's a link in capraise_dealroom_links
      const { data: linkData, error: linkError } = await supabase
        .from('capraise_dealroom_links')
        .select('dataroom_project_id, dataroom_projects(*)')
        .eq('round_id', activeRound.id)
        .single();

      if (!linkError && linkData) {
        setDataroomProject(linkData.dataroom_projects);
        await loadProjectStats(linkData.dataroom_project_id);
        return;
      }

      // Fallback: search by project name pattern (try both old and new patterns)
      const newProjectNamePattern = `${selectedCompany?.company_name} - ${activeRound.round_name} Deal Room`;
      const oldProjectNamePattern = `${selectedCompany?.company_name} - ${activeRound.round_name} DealRoom`; // Old pattern without space
      
      
      // Try new pattern first
      let { data: existingProjects, error: fetchError } = await supabase
        .from('dataroom_projects')
        .select('*')
        .ilike('project_name', newProjectNamePattern);

      // If no results with new pattern, try old pattern
      if (!fetchError && (!existingProjects || existingProjects.length === 0)) {
        const { data: oldProjects, error: oldError } = await supabase
          .from('dataroom_projects')
          .select('*')
          .ilike('project_name', oldProjectNamePattern);
        
        if (!oldError) {
          existingProjects = oldProjects;
          fetchError = oldError;
        }
      }

      if (fetchError) {
        throw fetchError;
      }

      if (existingProjects && existingProjects.length > 0) {
        const existingProject = existingProjects[0];
        setDataroomProject(existingProject);
        
        // Create link if it doesn't exist
        await supabase
          .from('capraise_dealroom_links')
          .upsert({
            round_id: activeRound.id,
            dataroom_project_id: existingProject.id,
            is_active: true
          });
        
        // Load project statistics
        await loadProjectStats(existingProject.id);
      } else {
        // Try alternative search by description and flexible name matching
        
        // Search by description containing round name
        const { data: descProjects } = await supabase
          .from('dataroom_projects')
          .select('*')
          .ilike('description', `%${activeRound.round_name}%`);

        // Search by project name containing company name
        const { data: nameProjects } = await supabase
          .from('dataroom_projects')
          .select('*')
          .ilike('project_name', `%${selectedCompany?.company_name}%`);

        // Combine results and remove duplicates
        const allProjects = [...(descProjects || []), ...(nameProjects || [])];
        const uniqueProjects = allProjects.filter((project, index, self) => 
          index === self.findIndex(p => p.id === project.id)
        );


        if (uniqueProjects.length > 0) {
          // Find the most likely match (prefer projects with both company and round name)
          const bestMatch = uniqueProjects.find(project => 
            project.project_name?.includes(selectedCompany?.company_name) && 
            project.project_name?.includes(activeRound.round_name)
          ) || uniqueProjects[0];

          setDataroomProject(bestMatch);
          
          // Create link
          await supabase
            .from('capraise_dealroom_links')
            .upsert({
              round_id: activeRound.id,
              dataroom_project_id: bestMatch.id,
              is_active: true
            });
          
          await loadProjectStats(bestMatch.id);
        } else {
          setDataroomProject(null);
        }
      }

    } catch (error) {
      console.error('Error loading dataroom project:', error);
      
      // Check if it's a permission error
      if (error.message?.includes('permission') || error.message?.includes('RLS')) {
        addMessage?.('You do not have permission to access dataroom projects. Please contact your administrator.', 'error');
      } else {
        addMessage?.('Error loading dataroom project: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [activeRound, selectedCompany, addMessage]);

  // Load project statistics
  const loadProjectStats = async (projectId) => {
    try {
      // Get document count
      const { count: docCount, error: countError } = await supabase
        .from('dataroom_items')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .eq('is_folder', false); // Files are not folders

      if (countError) {
        console.warn('Could not load document count:', countError);
      }

      // Get total views and file sizes
      const { data: items, error: itemsError } = await supabase
        .from('dataroom_items')
        .select('file_size, access_count')
        .eq('project_id', projectId)
        .eq('is_folder', false); // Files are not folders

      if (itemsError) {
        console.warn('Could not load item stats:', itemsError);
      }

      const totalViews = items?.reduce((sum, item) => sum + (item.access_count || 0), 0) || 0;
      const storageUsed = items?.reduce((sum, item) => sum + (item.file_size || 0), 0) || 0;

      setProjectStats({
        totalDocuments: docCount || 0,
        totalViews,
        totalDownloads: 0, // No download_count column exists
        storageUsed
      });
    } catch (error) {
      console.error('Error loading project stats:', error);
      // Set default stats if there's an error
      setProjectStats({
        totalDocuments: 0,
        totalViews: 0,
        totalDownloads: 0,
        storageUsed: 0
      });
    }
  };

  // Create new dataroom project for this round
  const handleCreateProject = async () => {
    if (!activeRound || !selectedCompany) {
      addMessage?.('Please select a company and round first', 'error');
      return;
    }

    try {
      setCreatingProject(true);

      const projectData = {
        project_name: `${selectedCompany.company_name} - ${activeRound.round_name} Deal Room`,
        description: `Document management for ${selectedCompany.company_name}'s ${activeRound.round_name} fundraising round`,
        color: '#3B82F6' // Default blue color
      };


      const { data: newProject, error } = await supabase
        .from('dataroom_projects')
        .insert([projectData])
        .select()
        .single();

      if (error) throw error;

      // Add the creator to the project
      const { error: userError } = await supabase
        .from('dataroom_users')
        .insert({
          user_id: user.id,
          project_id: newProject.id,
          role: 'creator',
          status: 'active'
        });

      if (userError) {
        console.warn('Failed to add creator to project:', userError);
      }

      // Create link between round and dataroom project
      const { error: linkError } = await supabase
        .from('capraise_dealroom_links')
        .insert({
          round_id: activeRound.id,
          dataroom_project_id: newProject.id,
          is_active: true,
          auto_grant_access: false,
          default_permission_level: 'view'
        });

      if (linkError) {
        console.warn('Failed to create dealroom link:', linkError);
      }

      setDataroomProject(newProject);
      addMessage?.('Deal room project created successfully', 'success');
      
      // Load initial stats
      await loadProjectStats(newProject.id);

    } catch (error) {
      console.error('Error creating dataroom project:', error);
      
      // Check if it's a permission error
      if (error.message?.includes('permission') || error.message?.includes('RLS')) {
        addMessage?.('You do not have permission to create dataroom projects. Please contact your administrator.', 'error');
      } else {
        addMessage?.('Error creating deal room project: ' + error.message, 'error');
      }
    } finally {
      setCreatingProject(false);
    }
  };

  // Load available projects for manual selection
  const loadAvailableProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setAvailableProjects(data || []);
    } catch (error) {
      console.error('Error loading available projects:', error);
    }
  };

  // Clear existing link and allow manual selection
  const handleClearLink = async () => {
    try {
      // Delete the existing wrong link
      const { error } = await supabase
        .from('capraise_dealroom_links')
        .delete()
        .eq('round_id', activeRound.id);

      if (error) throw error;

      setDataroomProject(null);
      addMessage?.('Link cleared. Please select the correct project.', 'success');
    } catch (error) {
      console.error('Error clearing link:', error);
      addMessage?.(`Error clearing link: ${error.message}`, 'error');
    }
  };

  // Manually select a project
  const handleSelectProject = async (projectId) => {
    try {
      const selectedProject = availableProjects.find(p => p.id === projectId);
      if (selectedProject) {
        setDataroomProject(selectedProject);
        
        // Create link
        await supabase
          .from('capraise_dealroom_links')
          .upsert({
            round_id: activeRound.id,
            dataroom_project_id: selectedProject.id,
            is_active: true
          });
        
        await loadProjectStats(selectedProject.id);
        setShowProjectSelector(false);
        addMessage?.('Dataroom project linked successfully', 'success');
      }
    } catch (error) {
      console.error('Error selecting project:', error);
      addMessage?.(`Error linking project: ${error.message}`, 'error');
    }
  };

  // Navigate to dataroom project
  const handleOpenDataroom = () => {
    if (dataroomProject) {
      navigate(`/dataroom/${dataroomProject.id}`);
    } else {
    }
  };

  useEffect(() => {
    loadDataroomProject();
  }, [loadDataroomProject]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!activeRound) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Round</h3>
        <p className="text-gray-500">Create or select a fundraising round to manage documents.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DealDocs</h1>
          <p className="text-gray-600">
            Document management for <span className="font-medium">{activeRound.round_name}</span>
          </p>
        </div>
        <div className="flex gap-3">
          {dataroomProject ? (
            <>
              <button
                onClick={handleOpenDataroom}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Deal Room
              </button>
              <button
                onClick={() => navigate(`/dataroom/${dataroomProject.id}/settings`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </button>
              <button
                onClick={handleClearLink}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Link
              </button>
            </>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleCreateProject}
                disabled={creatingProject}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingProject ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Deal Room
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  loadAvailableProjects();
                  setShowProjectSelector(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <FileText className="w-4 h-4 mr-2" />
                Select Existing
              </button>
            </div>
          )}
        </div>
      </div>

      {dataroomProject ? (
        <>
          {/* Project Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Documents</p>
                  <p className="text-2xl font-bold text-gray-900">{projectStats.totalDocuments}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Views</p>
                  <p className="text-2xl font-bold text-gray-900">{projectStats.totalViews}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Download className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Downloads</p>
                  <p className="text-2xl font-bold text-gray-900">{projectStats.totalDownloads}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Upload className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Storage Used</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(projectStats.storageUsed / 1024 / 1024)} MB
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Project Info */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Deal Room Project</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleOpenDataroom}
                  className="inline-flex items-center px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Project
                </button>
                <button
                  onClick={() => navigate('/dataroom/users')}
                  className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Users
                </button>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-600">Project Name</p>
                <p className="text-gray-900">{dataroomProject.project_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Description</p>
                <p className="text-gray-900">{dataroomProject.description}</p>
              </div>
              <div className="flex gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Created</p>
                  <p className="text-gray-900">
                    {new Date(dataroomProject.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    dataroomProject.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {dataroomProject.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={handleOpenDataroom}
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Folder className="w-8 h-8 text-blue-600 mr-3" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Browse Documents</p>
                  <p className="text-sm text-gray-600">View and manage files</p>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/dataroom/users')}
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Users className="w-8 h-8 text-green-600 mr-3" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Manage Users</p>
                  <p className="text-sm text-gray-600">Add and manage access</p>
                </div>
              </button>
              
              <button
                onClick={() => navigate(`/dataroom/${dataroomProject.id}/settings`)}
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Settings className="w-8 h-8 text-purple-600 mr-3" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Project Settings</p>
                  <p className="text-sm text-gray-600">Configure project options</p>
                </div>
              </button>
            </div>
          </div>
        </>
      ) : (
        /* No Project - Create One */
        <div className="text-center py-16 border-2 border-dashed border-gray-300 rounded-lg">
          <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Deal Room Created</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create a secure document room for your fundraising round to share files with investors.
          </p>
          <button
            onClick={handleCreateProject}
            disabled={creatingProject}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {creatingProject ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 mr-2" />
                Create Deal Room
              </>
            )}
          </button>
          
          <div className="mt-6 text-sm text-gray-500">
            <p><strong>Features included:</strong></p>
            <ul className="mt-2 space-y-1">
              <li>• Secure document sharing</li>
              <li>• User access control</li>
              <li>• View and download tracking</li>
              <li>• File organization and folders</li>
            </ul>
          </div>
        </div>
      )}

      {/* Project Selector Modal */}
      {showProjectSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Select Existing Dataroom Project</h3>
              <button
                onClick={() => setShowProjectSelector(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availableProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleSelectProject(project.id)}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 hover:border-blue-300"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{project.project_name}</h4>
                      <p className="text-sm text-gray-600">{project.description}</p>
                      <p className="text-xs text-gray-500">ID: {project.id}</p>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(project.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {availableProjects.length === 0 && (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No projects found</h3>
                <p className="mt-1 text-sm text-gray-500">No dataroom projects available.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealDocsPage;
